import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { Provider } from "./order";
import { ExternalReturnHubType } from "./team";

export type AddressType = Provider | "custom";

export const addressTypes = [...Object.values(Provider), "custom"] as const;

export const addressTypeSchema = z.union([
  createEnum(Provider, { enumName: "Provider" }),
  z.literal("custom"),
]);

// Base address fields (without integrations)
const BaseAddressSchema_ = z.object({
  name: z.string(),
  street1: z.string(),
  street2: z.string().optional(),
  city: z.string(),
  state: z.string().optional(),
  zip: z.string(),
  country: z.string(),
  phone: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  country_name: z.string().optional(),
});

export const BaseAddressSchema: z.ZodType<BaseAddress> = BaseAddressSchema_;
export interface BaseAddress extends z.infer<typeof BaseAddressSchema_> {}

const IntegrationsSchema_ = z.object({
  trackstarWarehouseId: z.string().optional(),
  aims360WarehouseId: z.string().optional(),
  inmarLocationId: z.string().optional(),
  blueYonderLocationId: z.string().optional(),
  isSekoHub: z.boolean().optional(),
  externalReturnHub: z
    .object({
      provider: createEnum(ExternalReturnHubType, {
        enumName: "ExternalReturnHubType",
      }),
      locationId: z.string().optional(),
    })
    .optional(),
});

export const IntegrationsSchema: z.ZodType<Integration> = IntegrationsSchema_;
export interface Integration extends z.infer<typeof IntegrationsSchema_> {}

export type ExternalReturnHub = Integration["externalReturnHub"];

export interface ExternalReturnHubInput {
  provider: ExternalReturnHubType;
  locationId?: string | null;
}

export interface FindExternalReturnHubParams {
  provider?: ExternalReturnHubType;
  locationId?: string;
}

function matchesExternalReturnHubParams(
  hub: ExternalReturnHubInput,
  params: FindExternalReturnHubParams,
): boolean {
  if (params.provider !== undefined && hub.provider !== params.provider) {
    return false;
  }
  if (params.locationId !== undefined && hub.locationId !== params.locationId) {
    return false;
  }
  return true;
}

export function findExternalReturnHub<T extends ExternalReturnHubInput>(
  externalReturnHub: T | null | undefined,
  params: FindExternalReturnHubParams,
): T | undefined {
  if (!externalReturnHub) {
    return undefined;
  }
  return matchesExternalReturnHubParams(externalReturnHub, params)
    ? externalReturnHub
    : undefined;
}

export function hasExternalReturnHub(
  externalReturnHub: ExternalReturnHubInput | null | undefined,
  params: FindExternalReturnHubParams,
): boolean {
  return findExternalReturnHub(externalReturnHub, params) !== undefined;
}

export const AddressCollectionSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  type: addressTypeSchema,
  externalId: z.string().optional(),
  address: BaseAddressSchema_.optional(),
  integrations: IntegrationsSchema_.optional(),
});

export const AddressCollectionSchema: z.ZodType<AddressCollection> =
  AddressCollectionSchema_;
export interface AddressCollection
  extends z.infer<typeof AddressCollectionSchema_> {}

export const CreateAddressCollectionSchema = AddressCollectionSchema_.omit({
  _id: true,
});

export interface CreateAddressCollectionInput
  extends z.infer<typeof CreateAddressCollectionSchema> {}

export const UpdateAddressCollectionSchema = AddressCollectionSchema_.omit({
  _id: true,
  team: true,
}).partial();
export interface UpdateAddressCollectionInput
  extends z.infer<typeof UpdateAddressCollectionSchema> {}
